import {
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid, GridCellEditStopReasons,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import Spinner from '../../components/spinner';
import StyledDataGrid from '../../styles/StyledDataGrid';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import { fDate } from '../../utils/formatTime';
import SpecToolbar from './SpecToolbar';

const currencyLocale = Intl.NumberFormat('ko-KR')

const TableSpec = props => {
  const {filters, submit, selected, setSelected, page, setPage, pageSize, setPageSize} = props;
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('desc');
  const [list, setList] = useState([]);
  const [sortColumn, setSortColumn] = useState('spec_time');

  const loadColumnSpecTable = window.localStorage.getItem('columnSpecTable') ? 
    JSON.parse(window.localStorage.getItem('columnSpecTable')) : 
    {
      spec_time: true,
    };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(loadColumnSpecTable);

  const loadSpec = async (sort, column) => {
    try {
      const response = await apiService.get('/proc/spec', {
        params: {
          ...filters,
          sort,
          column,
          page,
          pageSize
        }
      });
      const payload = response.data;

      setTotal(payload.size);
      setList(payload.list);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const {startDate, endDate} = filters;
    
    if(!isEmpty(startDate) && !isEmpty(endDate)) {
      loadSpec(sort, sortColumn);
    }
  }, [page, pageSize, reload, submit, filters])

  useEffect(() => {
    window.localStorage.setItem('columnSpecTable', JSON.stringify(columnVisibilityModel));
    setColumnVisibilityModel(columnVisibilityModel);
  }, [columnVisibilityModel])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadSpec(newModel[0].sort, newModel[0].field)
    } else {
      setSort('asc')
      setSortColumn('spec_time')
    }
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  const handleStateChange = async (cartid, field, newValue) => {
    const updatedValue = newValue;

    setList((prevList) =>
      prevList.map((item) =>
        item.cartid === cartid ? { ...item, [field]: updatedValue } : item
      )
    );
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      headerAlign: 'left'
    },
    {
      field: 'spec_time',
      hideable: false,
      headerName: '견적일자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {fDate(params.row.spec_time)}
        </Typography>
      )
    },
    {
      field: 'orderid',
      hideable: false,
      headerName: '견적번호',
      minWidth: 200,
      renderCell: params => (
        <Link to={`/admin/order/${params.row.orderid}`}>
          {params.row.orderid}
        </Link>
      )
    },
    {
      field: 'user_id',
      hideable: false,
      headerName: '회원 ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.user_id}
        </Typography>
      )
    },
    {
      field: 'sales_manager',
      hideable: false,
      headerName: '영업담당',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.sales_manager}
        </Typography>
      )
    },
    {
      field: 'business_type',
      hideable: false,
      headerName: '업체분류',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.business_type}
        </Typography>
      )
    },
    {
      field: 'company_name',
      hideable: false,
      headerName: '회사명',
      renderCell: params => (
        <Link to={`/admin/company/${params.row.com_id}`} >
          {params.row.company_name}
        </Link>
      )
    },
    {
      field: 'orderer',
      hideable: false,
      headerName: '주문자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.orderer}
        </Typography>
      )
    },
    {
      field: 'catalog_no',
      hideable: false,
      headerName: 'Cat No.',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.catalog_no}
        </Typography>
      )
    },
    {
      field: 'product_name',
      hideable: false,
      headerName: '제품명',
      minWidth: 200,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.product_name}
        </Typography>
      )
    },
    {
      field: 'cas_number',
      hideable: false,
      headerName: 'CAS',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.cas_number}
        </Typography>
      )
    },
    {
      minWidth: 300,
      field: 'specification',
      hideable: false,
      headerName: '사양',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.specification}
        </Typography>
      )
    },
    {
      field: 'packing',
      hideable: false,
      headerName: '패킹',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.packing}
        </Typography>
      )
    },
    {
      field: 'quantity',
      hideable: false,
      headerName: '수량',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.quantity}
        </Typography>
      )
    },
    {
      field: 'purchase_amount',
      hideable: false,
      headerName: '원가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.purchase_amount)}
        </Typography>
      )
    },
    {
      field: 'unit_price',
      hideable: false,
      headerName: '단가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.unit_price)}
        </Typography>
      )
    },
    {
      field: 'supply_price',
      hideable: false,
      headerName: '공급가액',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.supply_price)}
        </Typography>
      )
    },
    {
      field: 'order_amount',
      hideable: false,
      headerName: '주문가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.order_amount)}
        </Typography>
      )
    },
    {
      field: 'storage',
      hideable: false,
      headerName: '보관온도',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.storage}
        </Typography>
      )
    },
    {
      field: 'stock_kr',
      hideable: false,
      headerName: '국내재고',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.stock_kr}
        </Typography>
      )
    },
    {
      field: 'stock_intl',
      hideable: false,
      headerName: '해외재고',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.stock_intl}
        </Typography>
      )
    },
    {
      field: 'inquiry',
      hideable: false,
      headerName: '문의항목',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.inquiry}
        </Typography>
      )
    },
    {
      field: 'remarks',
      hideable: false,
      headerName: '메모*',
      editable: true,
      renderCell: (params) => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.remarks}
        </Typography>
      )
    },
    {
      field: 'order_status',
      minWidth: 150,
      hideable: false,
      headerName: '진행상황',      
      renderCell: params => {  
        return (
          <Select
            value={params.row.order_status}
            onChange={(e) => {
              handleStateChange(params.row.cartid, 'order_status', e.target.value);
            }}
            size="small"
            native
          >
            <option>신규견적</option>
            <option>견적완료</option>
            <option>미발주</option>
            <option>신규주문</option>
            <option>주문완료</option>
            <option>신고관리</option>
            <option>배송완료</option>
            <option>주문취소</option>
            <option>취소완료</option>
          </Select>
        )
      }
    }
  ]

  return (
    loading ?
    <Spinner/> :
    <StyledDataGrid>
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.orderid}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: SpecToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            numSelected: selected.length || 0,
            selected,
            setList,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.orderid),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
        onCellEditStop={(params, event) => {
          if (params.reason === GridCellEditStopReasons.enterKeyDown) {         
            if (event.target.value?.length === 0) {
              return
            }

            // change source data only with changed value
            setList(list.map(item => {
              if (item.cartid == params.id) {
                  return { ...item, 
                    [params.field]: event.target.value
                  };
              }
              return item;
            }));

            // 이미 선택된 row가 수정된 경우
            setSelected(
              list.filter(item => {
                if (item.cartid == params.id) {
                  return { ...item, 
                    [params.field]: event.target.value
                  };
              }
              return item;
            }));

          }
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
      />
    </StyledDataGrid>
  )
}

export default TableSpec